<template>
  <div class="mx-10 ma-5">
    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>
    <v-card flat class="pa-5">
      <div>
        <PageTitleWithSearch
          :title="'Payment Voucher'"
          :btnName="'Create New Payment Voucher'"
          :btnColor="'#00B3FF'"
          :btnRoute="'/payment-voucher/create'"
          :btnPermission="'payment_voucher.create'"
          :btnEnable="true"
          :loading="loading"
          :search="search"
          @searchText="searchChange($event)"
        />

        <template v-if="paymentVoucherList.length > 0">
          <DataTableWithPagination
            :headers="tableHeaders"
            :items="paymentVoucherList"
            :loading="loading"
            :page="page"
            :total="total"
            @pageChange="page = $event"
          >
            <template #row="{ item, index }">
              <tr
                :key="item.uuid"
                :class="[
                  { 'first-row': index === 0 },
                  index % 2 === 0 ? 'dark-row' : 'light-row',
                  'custom-row',
                ]"
              >
                <td class="text-center">{{ item.id }}</td>
                <td class="text-center">{{ item.po_number }}</td>
                <td class="text-center">{{ item.branch_code }}</td>
                <td class="text-center">{{ item.financial_year }}</td>
                <td class="text-center">{{ item.amount }}</td>
                <td class="text-center">{{ item.payment_type }}</td>
                <td class="text-center">
                  <v-btn
                    v-if="
                      item.is_post === 0 &&
                        hasPermission('payment_voucher.edit')
                    "
                    color="primary"
                    x-small
                    @click="postVoucher(item.uuid)"
                    class="ma-1"
                  >
                    <v-icon left small>mdi-check</v-icon>
                    Post
                  </v-btn>
                  <v-btn v-else color="grey" x-small disabled class="ma-1">
                    <v-icon left small>mdi-check</v-icon>
                    Posted
                  </v-btn>
                </td>
                <td class="text-center">
                  <v-btn
                    v-if="
                      item.is_post == 0 && hasPermission('payment_voucher.edit')
                    "
                    class="ma-1"
                    color="#FFE0B2"
                    :to="'/payment-voucher/edit/' + item.uuid"
                    fab
                    x-small
                    style="box-shadow: none;"
                  >
                    <v-icon small color="#FF9800">mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    v-else
                    class="ma-1"
                    disabled
                    fab
                    x-small
                    style="box-shadow: none;"
                    ><v-icon small color="#FF9800">mdi-pencil</v-icon></v-btn
                  >
                  <v-btn
                    v-if="
                      item.is_post == 0 &&
                        hasPermission('payment_voucher.delete')
                    "
                    class="my-1"
                    x-small
                    color="#FFCDD2"
                    @click.stop="dropPaymentVoucherConfirm(item.uuid)"
                    fab
                    style="box-shadow: none;"
                  >
                    <v-icon dark small color="#F44336">mdi-delete</v-icon>
                  </v-btn>
                  <v-btn
                    v-else
                    class="my-1"
                    disabled
                    fab
                    x-small
                    style="box-shadow: none;"
                  >
                    <v-icon small color="#FF9800">mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </DataTableWithPagination>
        </template>
        <template v-else>
          <v-alert type="info" class="mt-5" outlined>
            No payment vouchers found for the applied filters.
          </v-alert>
        </template>
      </div>
    </v-card>
    <ConfirmModal
      :text="'Do you want to drop this Payment Voucher?'"
      :title="'Drop Payment Voucher'"
      :color="'red'"
      :confirmOpen="confirmOpen"
      @cancelConfirm="cancelConfirm()"
      @confirm="confirm()"
    ></ConfirmModal>
    <Snackbars
      :text="successMsg"
      :snackbar="snackbar"
      :color="'warning'"
      @closeSnackbar="closeSnackbar"
    />
  </div>
</template>

<script>
import DataTableWithPagination from "@/components/shared/DataTableWithPagination.vue";
import PageTitleWithSearch from "@/components/shared/PageTitleWithSearch";
import { PaymentVoucherService } from "@/services/paymentVoucher-service.js"; // Adjust service name
import { checkError } from "@/config/errors.js";
import ConfirmModal from "@/components/shared/ConfirmModal";
import Snackbars from "@/components/shared/Snackbars";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");
const { mapGetters: mapSettingsGetters } = createNamespacedHelpers("settings");
import Alert from "@/components/shared/Alert.vue";

export default {
  name: "PaymentVoucherList",
  components: {
    PageTitleWithSearch,
    ConfirmModal,
    DataTableWithPagination,
    Snackbars,
    Alert,
  },
  data() {
    return {
      dropId: null,
      confirmOpen: false,
      snackbarColor: "success",
      snackbar: false,
      successMsg: "",
      errors: null,
      loading: false,
      search: "",
      paymentVoucherList: [],
      page: 1,
      total: 0,
      headers: [
        { text: "ID", align: "center", value: "id" },
        { text: "PO Number", align: "center", value: "po_number" },
        { text: "Branch code", align: "center", value: "branch_code" },
        { text: "Financial Year", align: "center", value: "financial_year" },
        { text: "Amount", align: "center", value: "amount", isCurrency: true },
        { text: "Payment Type", align: "center", value: "payment_type" },
        { text: "Status", align: "center", value: "status" },
        { text: "Actions", align: "center", value: "actions" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
    ...mapSettingsGetters(["currencyCode"]),
    tableHeaders() {
      return this.headers.map((h) => {
        if (h.isCurrency) {
          return {
            ...h,
            text: `${h.text} (${this.currencyCode})`,
          };
        }
        return h;
      });
    },
  },
  created() {
    this.fetchPaymentVoucherList();
  },
  watch: {
    search() {
      this.page = 1;
      this.fetchPaymentVoucherList();
    },
    page() {
      this.fetchPaymentVoucherList();
    },
  },
  methods: {
    async fetchPaymentVoucherList() {
      this.loading = true;
      try {
        const response = await PaymentVoucherService.getPaymentVoucherList(
          this.search,
          this.page
        );
        this.paymentVoucherList = response.data?.data || [];
        this.total = response.data?.meta?.last_page || 0;
      } catch (error) {
        this.paymentVoucherList = [];
        this.total = 0;
        console.error("Error fetching Payment Voucher List:", error);
      } finally {
        this.loading = false;
      }
    },

    async postVoucher(uuid) {
      try {
        await PaymentVoucherService.postVoucher(uuid);
        this.successMsg = "Voucher posted successfully.";
        this.snackbar = true;
        this.fetchPaymentVoucherList();
      } catch (error) {
        console.error("Error posting voucher:", error);
        this.successMsg = "Failed to post voucher.";
        this.snackbar = true;
      }
    },

    searchChange(event) {
      this.search = event;
    },

    dropPaymentVoucherConfirm(uuid) {
      this.dropId = uuid;
      this.confirmOpen = true;
    },

    cancelConfirm() {
      this.confirmOpen = false;
    },

    confirm() {
      this.confirmOpen = false;
      this.dropMember();
    },
    dropMember() {
      if (this.dropId) {
        this.loading = true;
        PaymentVoucherService.dropMember(this.dropId)
          .then((response) => {
            console.log(response);
            this.snackbarColor = "success";
            this.successMsg = "Payment Voucher deleted successfully.";
            this.snackbar = true;
            this.fetchPaymentVoucherList();
          })
          .catch((error) => {
            let data = error.response?.data?.error || "Unknown error";

            let errorOutput = checkError.errorFormatting(data);
            let errorsArray = [];
            if (Array.isArray(errorOutput)) {
              errorsArray = errorOutput.filter((e) => !!e && e.trim());
              if (errorsArray.length === 0) errorsArray = [data];
            } else if (errorOutput && errorOutput.trim()) {
              errorsArray = [errorOutput];
            } else {
              errorsArray = [data];
            }
            this.errors = errorsArray;
          })
          .finally(() => {
            this.loading = false;
            this.dropId = null; // Reset dropId
          });
      }
    },

    closeSnackbar() {
      this.snackbar = false;
    },
    onDismissed() {
      this.errors = null;
    },
  },
};
</script>
